import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { identifyUser, resetUser, trackEvent } from "../lib/analytics";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      identifyUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, AuthCallback exchanges the session_id first — skip the /me probe.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (source = "unknown") => {
    trackEvent("login_started", { method: "custom", source });
    // Route to our own /login page — no more Emergent-managed OAuth redirect.
    window.location.href = "/login";
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing local session anyway.", e);
    }
    trackEvent("logout");
    resetUser();
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
